<template>
  <div class="w-full">
    <div
      v-if="!showList.length"
      class="user-select-placeholder"
      @click="openSelector"
    >
      <van-icon v-if="fieldIcon" :name="fieldIcon" class="placeholder-leading-icon" />
      <span>{{ placeholder }}</span>
      <van-icon name="arrow" class="placeholder-arrow" />
    </div>
    <MultiSelectDisplay
      v-else
      :list="showList"
      :closable="editable"
      :item-font-size="selectedFontSize"
      :soft="softSelectedItems"
      @click="openSelector"
      @close="handleRemove"
    />
    <van-popup
      v-model="showPopup"
      position="bottom"
      round
      class="user-select-popup"
      :style="{height: panelHeight}"
    >
      <div class="container">
        <div>
          <AsyncList :fn="fetchVUserMsls" :page-size="20">
            <template v-slot="{list, refresh}">
              <div ref="selectorHeader" class="selector-header">
                <div class="selector-toolbar">
                  <div class="van-picker__cancel" @click="showPopup = false">
                    {{ $t('btn_cancel') }}
                  </div>
                  <div class="selector-title">
                    {{ title || $t('user_selector_title') }}
                    <span v-if="multiple && selected.length" class="selected-count">
                      {{ $t('user_selector_selected_count', {count: selected.length}) }}
                    </span>
                  </div>
                  <div
                    class="van-picker__confirm"
                    @click="confirmSelection(list)"
                  >
                    {{ $t('btn_sure') }}
                  </div>
                </div>
                <van-search
                  v-model="searchKey"
                  :placeholder="$t('user_selector_search_placeholder')"
                  shape="round"
                  @search="refresh"
                  @input="refresh"
                />
              </div>
              <component
                :is="multiple ? 'van-checkbox-group' : 'van-radio-group'"
                v-model="selected"
              >
                <section
                  v-for="group in groupedUsers(list)"
                  :key="group.name"
                  class="user-group"
                >
                  <div
                    v-if="groupByDepartment"
                    class="department-heading"
                    :style="{top: `${selectorHeaderHeight}px`}"
                  >
                    <span>{{ group.name }}</span>
                    <span class="department-count">{{ group.users.length }}</span>
                  </div>
                  <van-cell-group>
                    <van-cell
                      v-for="item in group.users"
                      :key="item[compareKey]"
                      clickable
                      class="user-cell"
                      @click="toggle(item)"
                    >
                      <template #icon>
                        <div class="user-avatar">{{ getUserInitial(item) }}</div>
                      </template>
                      <template #title>
                        <div class="user-name">{{ item.user_name }}</div>
                        <div v-if="item.position" class="user-position">
                          {{ item.position }}
                        </div>
                      </template>
                      <template #right-icon>
                        <van-checkbox
                          v-if="multiple"
                          :name="item[compareKey]"
                          checked-color="#aa341b"
                          @click.stop
                        />
                        <van-radio
                          v-else
                          :name="item"
                          checked-color="#aa341b"
                          @click.stop
                        />
                      </template>
                    </van-cell>
                  </van-cell-group>
                </section>
              </component>
            </template>
          </AsyncList>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import Vue from 'vue';
import {
  Cell,
  CellGroup,
  Checkbox,
  CheckboxGroup,
  Icon,
  Radio,
  RadioGroup,
  Search,
} from 'vant';
import AsyncList from '@/components/AsyncList.vue';
import {fetchVUserMsls} from '@/api/kol';
import MultiSelectDisplay from './MultiSelectDisplay.vue';

Vue.use(Search);
Vue.use(Icon);
Vue.use(Cell);
Vue.use(CellGroup);
Vue.use(Checkbox);
Vue.use(CheckboxGroup);
Vue.use(Radio);
Vue.use(RadioGroup);

export default {
  components: {
    AsyncList,
    MultiSelectDisplay,
  },
  props: {
    multiple: {
      type: Boolean,
      default: true,
    },
    editable: {
      type: Boolean,
      default: true,
    },
    groupByDepartment: {
      type: Boolean,
      default: false,
    },
    panelHeight: {
      type: String,
      default: '60vh',
    },
    title: {
      type: String,
      default: '',
    },
    selectedFontSize: {
      type: String,
      default: '14px',
    },
    softSelectedItems: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    fieldIcon: {
      type: String,
      default: '',
    },
    value: null,
    fn: Function,
    type: String,
  },
  data() {
    return {
      list: [],
      selected: [],
      showPopup: false,
      compareKey: 'user_id',
      searchKey: '',
      selectedUserMap: {},
      selectorHeaderHeight: 0,
      selectorHeaderObserver: null,
    };
  },
  computed: {
    showList() {
      const list = Array.isArray(this.value)
        ? this.value
        : [this.value].filter(Boolean);

      return list.map((item) => item.user_name);
    },
  },
  beforeDestroy() {
    this.selectorHeaderObserver?.disconnect?.();
  },
  methods: {
    openSelector() {
      if (!this.editable) return;
      const currentUsers = Array.isArray(this.value)
        ? this.value
        : [this.value].filter(Boolean);
      this.selectedUserMap = currentUsers.reduce((result, item) => ({
        ...result,
        [item[this.compareKey]]: item,
      }), {});
      this.selected = this.multiple
        ? this.value?.map(item => item[this.compareKey]) || []
        : this.value;
      this.showPopup = true;
      this.observeSelectorHeader();
    },
    handleRemove(index) {
      if (!this.editable) return;
      if (this.multiple) {
        const nextValue = [...(this.value || [])];
        nextValue.splice(index, 1);
        this.$emit('input', nextValue);
      } else {
        this.$emit('input', undefined);
      }
    },
    observeSelectorHeader() {
      this.selectorHeaderObserver?.disconnect?.();
      this.$nextTick(() => {
        window.requestAnimationFrame(() => {
          const header = this.$refs.selectorHeader;
          if (!header) return;

          const updateHeight = () => {
            this.selectorHeaderHeight = Math.ceil(header.getBoundingClientRect().height);
          };
          updateHeight();

          if (typeof ResizeObserver !== 'undefined') {
            this.selectorHeaderObserver = new ResizeObserver(updateHeight);
            this.selectorHeaderObserver.observe(header);
          }
        });
      });
    },
    fetchVUserMsls(pagination) {
      const fn = this.fn || fetchVUserMsls;
      return fn(this.searchKey, pagination).then((items) => {
        (items || []).forEach((item) => {
          this.selectedUserMap[item[this.compareKey]] = item;
        });
        return items;
      });
    },
    confirmSelection(list) {
      const result = this.multiple
        ? this.selected.map((id) =>
          list.find(listItem => listItem[this.compareKey] === id) ||
            this.selectedUserMap[id] ||
            this.value?.find(valueItem => valueItem[this.compareKey] === id),
        ).filter(Boolean)
        : this.selected;
      this.showPopup = false;
      this.$emit('input', result);
    },
    groupedUsers(list = []) {
      if (!this.groupByDepartment) {
        return [{name: 'all', users: list}];
      }

      const groups = new Map();
      list.forEach((item) => {
        const department = String(item?.department_name || '').trim() || this.$t('user_selector_unknown_department');
        if (!groups.has(department)) {
          groups.set(department, []);
        }
        groups.get(department).push(item);
      });

      return Array.from(groups.entries())
        .sort(([departmentA], [departmentB]) => departmentA.localeCompare(departmentB, this.$i18n.locale))
        .map(([name, users]) => ({
          name,
          users: [...users].sort((userA, userB) =>
            String(userA?.user_name || '').localeCompare(String(userB?.user_name || ''), this.$i18n.locale),
          ),
        }));
    },
    getUserInitial(item) {
      const name = String(item?.user_name || '').trim();
      return name ? name.slice(0, 1).toUpperCase() : '?';
    },
    toggle(item) {
      if (!this.multiple) {
        this.selected = this.selected === item ? undefined : item;
        return;
      }

      const id = item[this.compareKey];
      this.selectedUserMap[id] = item;
      const selectedIndex = this.selected.indexOf(id);
      if (selectedIndex > -1) {
        this.selected = this.selected.filter(selectedId => selectedId !== id);
      } else {
        this.selected = [...this.selected, id];
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.user-select-popup {
  overflow: hidden;
}

.user-select-placeholder {
  width: 100%;
  min-height: 34px;
  display: flex;
  align-items: center;
  color: #a7a9ad;
  font-size: 15px;
  cursor: pointer;
}

.placeholder-leading-icon {
  flex-shrink: 0;
  margin-right: 10px;
  color: #8f9297;
  font-size: 21px;
}

.placeholder-arrow {
  flex-shrink: 0;
  margin-left: auto;
  color: #8f9297;
  font-size: 17px;
}

.container {
  height: 100%;
  overflow-y: auto;
  background: #f6f7f9;
}

.selector-header {
  position: sticky;
  top: 0;
  z-index: 99;
  padding-top: 8px;
  background: #fff;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.05);

  &::after {
    content: '';
    position: absolute;
    right: 0;
    bottom: -1px;
    left: 0;
    height: 2px;
    background: #fff;
  }
}

.selector-toolbar {
  min-height: 48px;
  padding: 0 16px;
  display: grid;
  grid-template-columns: 64px 1fr 64px;
  align-items: center;
  font-size: 15px;
}

.selector-header ::v-deep .van-search {
  padding: 6px 12px 10px;
  background: #fff !important;
}

.selector-header ::v-deep .van-search__content {
  padding-left: 12px;
  border: 0;
  border-radius: 14px;
  background: #f5f6f8;
}

.selector-header ::v-deep .van-search .van-cell {
  padding: 6px 8px 6px 0;
  background: transparent;
}

.selector-header ::v-deep .van-search .van-cell__value {
  min-height: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  border: 0 !important;
  border-radius: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
}

.selector-title {
  min-width: 0;
  color: #222;
  font-size: 17px;
  font-weight: 600;
  text-align: center;
}

.selected-count {
  display: block;
  margin-top: 2px;
  color: #aa341b;
  font-size: 11px;
  font-weight: 400;
}

.van-picker__confirm {
  text-align: right;
}

.user-group {
  margin-top: 0;
}

.department-heading {
  position: sticky;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  color: #666;
  font-size: 13px;
  font-weight: 600;
  background: #f6f7f9;
}

.department-count {
  min-width: 20px;
  padding: 1px 6px;
  border-radius: 10px;
  color: #999;
  font-size: 11px;
  font-weight: 400;
  text-align: center;
  background: #e9ebef;
}

.user-cell {
  min-height: 64px;
}

.user-avatar {
  width: 38px;
  height: 38px;
  margin-right: 12px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #aa341b;
  font-size: 15px;
  font-weight: 600;
  background: #fbeeea;
}

.user-name {
  color: #222;
  font-size: 15px;
  line-height: 22px;
}

.user-position {
  margin-top: 2px;
  color: #999;
  font-size: 12px;
  line-height: 18px;
}

.container ::v-deep .van-cell__title {
  text-align: left;
}

.container ::v-deep .van-cell {
  display: flex !important;
  align-items: center;
}

::v-deep .van-cell__value {
  border: none !important;
  margin: none !important;
}
</style>
